import { dashboardApiClient } from './api'

/**
 * Billing actions and the one thing that makes the UI honest: after Checkout,
 * the browser coming back is not proof of payment. Stripe's webhook is what
 * records the subscription, and it can land before or after the redirect. So
 * the page polls until the suite really is paid for rather than assuming.
 */

export interface SuiteStatusData {
  suite: string | null
  tier?: string
  planSlug?: string
  source?: 'subscription' | 'grant' | 'free'
  /** A live subscription to the suite exists, whatever sets the tier. */
  subscribed?: boolean
  /** The live subscription's own plan; null when there is none. Unlike `planSlug`, a running Pro grant cannot mask it. */
  subscriptionPlanSlug?: string | null
}

export async function getSuiteStatus(suite: string): Promise<SuiteStatusData> {
  return dashboardApiClient.getTenantStatus(suite)
}

export async function startCheckout(body: {
  planSlug: string
  seats: number
  organizationName: string
  billingEmail: string
}): Promise<string> {
  const { url } = await dashboardApiClient.createCheckoutSession(body)
  return url
}

export async function cancelSubscription(
  suite: string,
  timing: 'period_end' | 'immediate'
): Promise<{ timing: string; accessEndsAt: string | null }> {
  return dashboardApiClient.cancelSubscription(suite, timing)
}

// ─── What the customer sees of their Stripe account ──────────────────────────
// Read live from Stripe on every open; nothing is cached locally. Edits
// (cards, billing email, address) happen in Stripe's billing portal.

export interface InvoiceSummary {
  id: string
  /** Stripe's invoice number; null until finalized (drafts are never listed). */
  number: string | null
  /** Stripe's own status: open, paid, void, uncollectible. */
  status: string
  /** ISO 4217, lower-case as Stripe reports it. */
  currency: string
  totalCents: number
  amountPaidCents: number
  amountDueCents: number
  createdAt: string
  periodStart: string | null
  periodEnd: string | null
  description: string | null
  /** Stripe's hosted page: view the invoice, and pay it while it is open. */
  hostedInvoiceUrl: string | null
  pdfUrl: string | null
}

export interface InvoicesView {
  /** Newest first. */
  invoices: InvoiceSummary[]
  /** True when there are more than one page holds. */
  hasMore: boolean
  /** False until the organization has a Stripe customer — the portal has nothing to open. */
  hasBillingAccount: boolean
}

export interface PaymentMethodSummary {
  id: string
  /** Stripe's type: card, us_bank_account, sepa_debit, link, … */
  type: string
  brand: string | null
  last4: string | null
  expMonth: number | null
  expYear: number | null
  /** The one Stripe will charge. */
  isDefault: boolean
}

export interface BillingAddress {
  line1: string | null
  line2: string | null
  city: string | null
  state: string | null
  postalCode: string | null
  country: string | null
}

export interface BillingAccount {
  /** False until the organization has a Stripe customer, i.e. has started Checkout once. */
  hasBillingAccount: boolean
  billingEmail: string | null
  billingName: string | null
  address: BillingAddress | null
  paymentMethods: PaymentMethodSummary[]
}

export async function listInvoices(): Promise<InvoicesView> {
  return dashboardApiClient.getInvoices()
}

export async function getBillingAccount(): Promise<BillingAccount> {
  return dashboardApiClient.getBillingAccount()
}

export type PortalReturnTab = 'overview' | 'subscription' | 'invoices' | 'payment-methods'

/**
 * Send the browser to Stripe's billing portal. On success the page is left
 * for Stripe's, and the portal returns the browser to `returnTab` when done.
 * Rejects when the session could not be made; the caller reports it.
 */
export async function openBillingPortal(opts: {
  flow?: 'payment_method_update'
  returnTab: PortalReturnTab
}): Promise<void> {
  const { url } = await dashboardApiClient.createPortalSession(opts)
  window.location.assign(url)
}

export type ConfirmOutcome = 'active' | 'timeout'

interface PollOptions {
  suite?: string
  timeoutMs?: number
  intervalMs?: number
  signal?: AbortSignal
}

/**
 * Poll the suite's status until `done` says so, or the deadline passes.
 *
 * Bounded on purpose. Polling forever would leave someone watching a spinner
 * with no idea whether anything is wrong; giving up after a minute lets the UI
 * say "we're still processing, refresh shortly", which is both true and
 * actionable. Nothing is lost either way — the webhook will land and the next
 * page load will show it.
 */
async function pollSuiteStatus(
  done: (status: SuiteStatusData) => boolean,
  opts: PollOptions
): Promise<boolean> {
  const suite = opts.suite ?? 'service'
  const timeoutMs = opts.timeoutMs ?? 60_000
  const intervalMs = opts.intervalMs ?? 2_000
  const deadline = Date.now() + timeoutMs

  while (Date.now() < deadline) {
    if (opts.signal?.aborted) return false
    try {
      if (done(await getSuiteStatus(suite))) return true
    } catch {
      // A failed poll is not a failed payment. Keep waiting; the deadline is
      // what ends this, not one bad request.
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
  }
  return false
}

/** Wait for the webhook to record the suite's subscription after Checkout. */
export async function waitForActivation(opts: PollOptions = {}): Promise<ConfirmOutcome> {
  // `subscribed`, not the tier's source: a purchase made during a trial is
  // recorded while the trial still sets the tier.
  return (await pollSuiteStatus((status) => status.subscribed === true, opts)) ? 'active' : 'timeout'
}

export type ChangeOutcome = 'changed' | 'timeout'

/**
 * Wait for the webhook to record a tier change. Only the webhook writes what
 * is bought, so until it lands the page would show the old plan — and a second
 * click on Switch would be refused as "already on that plan".
 *
 * Polls the subscription's own plan, not the suite's effective one: a running
 * Pro grant on top of the subscription masks the effective plan, so a switch
 * to Pro would read as done before the webhook lands and a switch away from
 * Pro would never read as done.
 */
export async function waitForPlanChange(
  opts: PollOptions & { planSlug: string }
): Promise<ChangeOutcome> {
  return (await pollSuiteStatus((status) => status.subscriptionPlanSlug === opts.planSlug, opts)) ? 'changed' : 'timeout'
}
